<template>
  <div class="component-roomtypes">
    <b-modal
      id="languageModal"
      ref="languageModal"
      no-fade
      centered
      static
      size="lg"
      modal-class="form-modal"
      :ok-title="$t(`buttons.${language.id != null ? 'update' : 'save'}`)"
      ok-variant="primary"
      :cancel-title="$t('buttons.cancel')"
      cancel-variant="outline-primary"
      :ok-disabled="updatePending || !$refs.languageForm || !formValid || !$refs.languageForm.flags.valid"
      :cancel-disabled="updatePending"
      :no-close-on-esc="updatePending"
      :no-close-on-backdrop="updatePending"
      :hide-header-close="updatePending"
      @show="modalScroll"
      @hidden="modalScroll"
      @ok.prevent="processForm"
    >
      <template #modal-header-close>
        <icon width="20" height="20" class="d-none d-md-block" type="times"/>
        <icon width="10" height="18" class="d-md-none" type="arrow-left"/>
      </template>
      <template #modal-title>
        {{ modalTitle }}
      </template>
      <ValidationObserver ref="languageForm" slim>
        <div class="edge edge-amount" >
          <h3 :content="$t('pages.languages.modal.section-iso')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="text" id="iso" name="iso" class="mb-0" no-icon no-tooltip
                v-model="language.iso"
                :disabled="updatePending"
                ref="fieldAmount"
                :rules="rulesFor('iso')"
              />
            </div>
          </div>
        </div>
        <div class="edge edge-amount">
          <h3 :content="$t('pages.languages.modal.section-name')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="text" id="name" name="name" class="mb-0" no-icon no-tooltip
                v-model="language.name"
                :disabled="updatePending"
                ref="fieldAmount"
                :rules="rulesFor('name')"
              />
            </div>
          </div>
        </div>
      </ValidationObserver>
    </b-modal>

    <div class="panel position-relative panel-content">
      <Breadcrumb :items=breadCrumbs() v-if="loaded"/>
      <div class="list d-none d-md-block left-edge">
        <p class="head-line"><span>{{ $t('pages.languages.title') }}</span><spinner v-if="!loaded" /></p>
        <div class="plans-table" v-if="loaded">
          <table class="w-100">
            <thead>
              <tr>
                <th class="w-id">{{ $t('id') }}</th>
                <th class="w-name">{{ $t('pages.languages.headers.iso') }}</th>
                <th class="w-occupancy text-center">{{ $t('pages.languages.headers.name') }}</th>
                <th class="w-actions">{{ $t('actions') }}</th>
              </tr>
            </thead>

            <tbody v-if="noLanguages">
              <tr>
                <td colspan="4" class="w-empty">{{ $t('pages.languages.no-languages') }}</td>
              </tr>
            </tbody>
            <tbody v-for="row in languages" :key="row.id">
              <tr class="separator before"></tr>
              <tr>
                <td>
                  {{ row.id }}
                </td>
                <td>
                  <p>{{ row.iso }}</p>
                </td>
                <td class="text-center">
                  {{ row.name }}
                </td>
                <td class="actions">
                  <b-btn class="btn-icon btn-tiny" @click="editLanguage(row)" :disabled="updatePending">
                    <icon width="17" height="17" type="edit"/>
                  </b-btn>
                </td>
              </tr>
              <tr class="separator after"></tr>
            </tbody>
          </table>
        </div>
        <div v-if="loaded">
          <b-btn pill variant="outline-primary" class="add-new-plan"
                 @click="openCreateForm">
            <icon width="10" height="11" type="plus"/>
            {{ $t('pages.languages.button-add') }}
          </b-btn>
        </div>
      </div>
    </div>
    <div class="d-md-none add-new-plan-block" v-if="loaded">
      <b-btn pill variant="outline-primary" class="add-new-plan" @click="openCreateForm">
        <icon width="10" height="10" type="plus"/>
        {{ $t('pages.languages.button-add') }}
      </b-btn>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex';

  export default {
    name: 'Languages',
    props: {
      payload: {
        validator: (v) => ['object', 'undefined'].includes(typeof v),
        default: null,
      },
    },
    data: () => ({
      language: {},
    }),
    async created() {
      this.resetLanguageModal();
      await this.fetchData(true);
    },
    computed: {
      ...mapGetters('languages', ['loaded', 'noLanguages', 'languages']),
      ...mapState('languages', ['error', 'pending', 'updatePending']),
      formValid() {
        return true;
      },
      modalTitle() {
        if (this.language.id == null) {
          return this.$t('pages.languages.modal.title-add');
        }
        const { id, name } = this.language;
        return this.$t('pages.languages.modal.title-edit', { id, name });
      },
    },
    methods: {
      ...mapActions('languages', ['fetchData', 'createLanguage', 'updateLanguage', 'deleteLanguage']),

      modalScroll(ev) {
        const modal = ev.target;
        this.$nextTick(() => {
          if (modal != null) modal.scrollTop = 0;
        });
      },
      resetLanguageModal() {
        this.language = {
          iso: '',
          name: '',
        };
        if (this.$refs.languageForm != null) {
          this.$refs.languageForm.reset();
        }
      },
      openCreateForm() {
        this.resetLanguageModal();
        this.$nextTick(this.$refs.languageModal.show);
      },
      editLanguage(language) {
        this.language = {};
        this.resetLanguageModal();
        this.language = {
          ...this.language,
          ...JSON.parse(JSON.stringify(language)), // deep clone without bindings
        };
        this.$nextTick(() => {
          this.$refs.languageForm.reset();
          this.$refs.languageModal.show();
        });
      },
      async processForm() {
        const language = { ...this.language };
        if (this.payload != null) {
          Object.keys(this.payload).forEach((k) => {
            language[k] = this.payload[k];
          });
        }
        try {
          if (language.id != null) {
            await this.updateLanguage(language);
          } else {
            await this.createLanguage(language);
          }
          this.$refs.languageModal.hide();
          this.$toastr.s({
            title: language.id ? this.$t('pages.languages.alert-edit') : this.$t('pages.languages.alert-saved'),
            msg: '',
            timeout: 3000,
            progressbar: false,
          });
          await this.fetchData(true);
        } catch (error) {
          this.$toastr.e(error.message, this.$t('error'));
        }
      },
      rulesFor(field) {
        const rules = {
          required: true,
        };
        switch (field) {
          case 'iso':
            break;
          case 'name':
            break;
          default:
            break;
        }
        return rules;
      },
      breadCrumbs() {
        return [
          {
            text: this.$t('pages.dashboard.title'),
            to: { name: 'dashboard' },
          },
          {
            text: this.$t('pages.languages.title'),
            active: true,
          },
        ];
      },
    },
  };
</script>
